import { action, observable } from 'mobx'
import { message } from 'hljd'
import api from '@api/vr'
import move from './move'
import vrStore from '../store'
import iconList from './icon'

const isMobile = () =>
  /(iPhone|iPad|iPod|iOS|Android|webOS|BlackBerry)/i.test(navigator.userAgent)

// 热点文本name前缀
const HOT_SPOT_TITLE_PREFIX = 'vrtooltip_'
// X符号图层name前缀
const DELETE_LAYER_NAME = 'deleteLayer'

class Store {
  @observable panoId = ''
  @observable pano = null
  @observable panoDetail = {} // vr详情
  @observable scenes = [] // vr场景信息
  @observable iconList = iconList // 热点图标列表
  @observable iconIndex = 0

  @observable currentSceneIndex = 0 // 当前在设置的场景
  // @observable currentLinkedSceneIndex = null // 当前高亮的连接场景
  @observable activeHotspotIndex = null // 当前选中的热点
  @observable isCurrentVr = true // 是否选中当前vr
  @observable allVrList = []
  @observable activeHotspotLinkedSceneName = null // 当前选中的热点链接的场景

  // 获取vr详情
  @action
  getPanoDetail = async () => {
    const { data } = await api.getPanoDetail({
      panoId: this.panoId
    })
    this.panoDetail = data
    this.scenes = data.scene.map(item => ({
      ...item,
      hotspots: JSON.parse(item.hotdata || '[]')
    }))
  }

  @action
  sortScene = async scenes => {
    const crrentSceneId = this.scenes[this.currentSceneIndex].id
    this.currentSceneIndex = scenes.findIndex(({ id }) => id === crrentSceneId)
    this.scenes = scenes
    const { success } = await api.sortScenes({
      panoId: this.panoId,
      panoScene: scenes.map(({ id }) => id)
    })
    if (success) {
      message.success('排序成功')
    }
  }

  // 保存当前场景热点信息
  @action
  onSave = async () => {
    const hotspots = this.getHotspots()
    const index = hotspots.findIndex(item => !item.linkedScene)
    if (index !== -1) {
      const { hlookat, vlookat } = this.pano.krGet('view')
      const { ath, atv } = hotspots[index]
      move(this.pano.krpano, hlookat, vlookat, ath, atv, () => {
        message.error('请设置热点跳转的全景图！')
      })
      this.activeHotspotIndex = index
      // this.currentLinkedSceneIndex = null
      this.activeHotspotLinkedSceneName = null
      return
    }
    const { success } = await api.postPanoHotdata({
      panoId: this.panoId,
      panoSceneId: this.getCurrentScene().id,
      hotdata: JSON.stringify(this.getHotspots())
    })
    if (success) {
      message.success('保存成功')
    }
    vrStore.isUnsave = false
  }

  @action
  addHotspotToScene = hotspot => {
    this.scenes[this.currentSceneIndex].hotspots.push(hotspot)
  }
  @action
  addLinkedSceneToHotspot = (hindex, sname) => {
    this.scenes[this.currentSceneIndex].hotspots[hindex].linkedScene = sname
  }
  @action getScene = name => this.scenes.find(item => item.name === name)
  @action
  getSceneIndex = name => this.scenes.findIndex(item => item.name === name)
  @action getCurrentScene = () => this.scenes[this.currentSceneIndex]
  @action getHotspots = () => this.getCurrentScene().hotspots
  @action
  getHotspot = name => this.getHotspots().find(item => item.name === name)
  @action
  getHotspotIndex = name =>
    this.getHotspots().findIndex(item => item.name === name)

  // 添加热点
  @action
  addHotspot = iconIndex => {
    const name = 'spot_' + Date.now()
    const { frameImg, animate, mob, pc } = this.iconList[iconIndex]
    const hlookat = this.pano.krGet('view.hlookat')
    const vlookat = this.pano.krGet('view.vlookat')
    this.pano.krCall(`addhotspot(${name})`)
    this.pano.krSet(`hotspot[${name}].url`, frameImg)
    this.pano.krSet(`hotspot[${name}].zoom`, true)
    this.pano.krSet(`hotspot[${name}].ath`, hlookat)
    this.pano.krSet(`hotspot[${name}].atv`, vlookat)
    this.pano.krSet(`hotspot[${name}].width`, isMobile() ? mob.w : pc.w)
    this.pano.krSet(`hotspot[${name}].height`, isMobile() ? mob.h : pc.h)
    this.pano.krSet(`hotspot[${name}].ondown`, 'draghotspot()')
    this.pano.krSet(`hotspot[${name}].onup`, () => this.updateHotspot(name))
    this.pano.krSet(`hotspot[${name}].onclick`, `js(onHotspotClick('${name}'))`)
    this.pano.krSet(
      `hotspot[${name}].onloaded`,
      `do_crop_animation(${animate.width},${animate.height},${
        animate.rate
      });add_all_the_time_tooltip_for_VR()`
    )

    // 清除已存在的delete layer
    this.pano.krCall(`removelayer(${DELETE_LAYER_NAME})`)
    // 清除图标说明
    this.updateHotSpotTitle()

    this.addHotspotToScene({
      name,
      animate,
      url: frameImg,
      ath: hlookat,
      atv: vlookat,
      linkedScene: '',
      mob,
      pc
    })
    const hotspots = this.getHotspots()
    this.activeHotspotIndex = hotspots.length - 1
    // this.currentLinkedSceneIndex = null
    this.activeHotspotLinkedSceneName = null
    vrStore.onDo()
  }

  // 更新拖拽热点的坐标信息
  @action
  updateHotspot = hname => {
    console.log('更新热点信息', hname)
    const ath = this.pano.krGet(`hotspot[${hname}].ath`)
    const atv = this.pano.krGet(`hotspot[${hname}].atv`)
    const hotspot = this.getHotspot(hname)
    hotspot.ath = ath
    hotspot.atv = atv
    this.activeHotspotIndex = this.getHotspotIndex(hname)
    this.initLinkedScene(hname)
    // 更新热点文字坐标
    this.updateHotSpotTitlePosition(hname)
  }

  // 初始化连接的场景 高亮
  @action
  initLinkedScene = hname => {
    const linkedScene = this.getHotspot(hname).linkedScene
    if (linkedScene) {
      // this.currentLinkedSceneIndex = this.getSceneIndex(linkedScene)
      this.activeHotspotLinkedSceneName = linkedScene
    } else {
      // this.currentLinkedSceneIndex = null
      this.activeHotspotLinkedSceneName = null
    }
  }

  // 给热点添加连接场景
  @action
  linkToScene = (sname, sceneIndex) => {
    if (this.activeHotspotIndex === null) {
      message.info('请先选择一个热点')
      return
    }
    this.addLinkedSceneToHotspot(this.activeHotspotIndex, sname)
    this.activeHotspotLinkedSceneName = sname
    vrStore.onDo()
  }

  // 切换场景
  @action
  switchScene = index => {
    this.currentSceneIndex = index
    const scene = this.getCurrentScene()
    this.pano.loadScene(scene)
    this.activeHotspotIndex = null
    // this.currentLinkedSceneIndex = null
    this.activeHotspotLinkedSceneName = null
  }

  /* 2020/06 全景图添加热点文本、删除符号 */

  /* 更新热点文字坐标 */
  updateHotSpotTitlePosition = hname => {
    const ath = this.pano.krGet(`hotspot[${hname}].ath`)
    const atv = this.pano.krGet(`hotspot[${hname}].atv`)
    this.pano.krSet(`hotspot[${HOT_SPOT_TITLE_PREFIX + hname}].ath`, ath)
    this.pano.krSet(`hotspot[${HOT_SPOT_TITLE_PREFIX + hname}].atv`, atv)
  }

  // 热点文本
  @observable hotSpotTitle
  /* 点击热点时，将当前文本更新到输入框 */
  @action
  updateHotSpotTitleByIndex = activeHotspotIndex => {
    const hotspots = this.getHotspots()
    const hotSpotTitle = hotspots[activeHotspotIndex].hotSpotTitle
    this.updateHotSpotTitle(hotSpotTitle)
  }

  /* 更新热点文本 */
  @action
  updateHotSpotTitle = hotSpotTitle => {
    this.hotSpotTitle = hotSpotTitle
  }

  /* 图标说明失焦时，更新热点文本内容及数据的更新 */
  @action
  blurHotSpotTitle = () => {
    try {
      const hotspots = this.getHotspots()
      const hname = hotspots[this.activeHotspotIndex].name
      this.pano.krSet(`hotspot[${hname}].text`, this.hotSpotTitle)
      this.pano.krSet(
        `hotspot[${HOT_SPOT_TITLE_PREFIX + hname}].visible`,
        !!this.hotSpotTitle
      )
      this.pano.krSet(
        `hotspot[${HOT_SPOT_TITLE_PREFIX + hname}].html`,
        this.hotSpotTitle
      )
      hotspots[this.activeHotspotIndex].hotSpotTitle =
        this.hotSpotTitle || undefined
    } catch (e) {
      message.error('请先选中图标！')
    }
  }

  /* 点击热点时，添加一个用于显示 X符号的layer，用于清除当前热点、热点文本、layer，即对数据的更新 */
  @action
  addLayer = hname => {
    this.pano.krCall(`addlayer(${DELETE_LAYER_NAME})`)
    this.pano.krSet(
      `layer[${DELETE_LAYER_NAME}].url`,
      'https://qnm.hunliji.com/FnpRKW4gpMo2q-tEva8RGP-jfsJy'
    )
    this.pano.krSet(`layer[${DELETE_LAYER_NAME}].align`, 'righttop')
    this.pano.krSet(`layer[${DELETE_LAYER_NAME}].width`, 32)
    this.pano.krSet(`layer[${DELETE_LAYER_NAME}].height`, 32)
    this.pano.krSet(`layer[${DELETE_LAYER_NAME}].ox`, 15)
    this.pano.krSet(`layer[${DELETE_LAYER_NAME}].oy`, -15)
    this.pano.krSet(`layer[${DELETE_LAYER_NAME}].parent`, `hotspot[${hname}]`)
    // 点击X时，需要清除当前热点、热点文本、layer，即对数据的更新
    this.pano.krSet(
      `layer[${DELETE_LAYER_NAME}].onclick`,
      `removehotspot(${hname});removehotspot(${HOT_SPOT_TITLE_PREFIX +
        hname});removelayer(${DELETE_LAYER_NAME});js(onDeleteHotSpot('${hname}')))`
    )
  }

  @action
  changeVr = e => {
    this.isCurrentVr = e.target.value
  }
  @action
  getAllVr = async () => {
    const { success, data } = await api.getAllVrList({
      panoId: this.panoId
    })
    if (success) {
      this.allVrList = data || []
    }
  }
}

export default new Store()
